import { Injectable, TemplateRef } from '@angular/core';

import { SetTab } from '@frontend/sports/betting-offer/feature/model';
import { DispatcherService } from '@frontend/sports/common/core/utils/dispatcher';
import { PopupAction } from '@frontend/sports/modal/feature';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { MarketUrlParam } from '@frontend/sports/navigation/core/feature/url-helper';

import { BetBuilderTrackingValues } from '../../../../lib/bet-builder/bet-builder-tracking';
import { BetBuilderEventModel } from '../../../../lib/bet-builder/model/bet-builder-event-model';
import { BetBuilderConfigService } from '../../../../lib/bet-builder/services/bet-builder-config.service';
import { BetBuilderOverlayService } from '../../../../lib/bet-builder/services/bet-builder-overlay.service';
import { DispatcherEvent } from '../../../../lib/event-details-common/event-details-common.models';
import { NavigationRouterService } from '../../../../lib/navigation/navigation-router.service';
import { PopupManager } from '../../../../lib/popup/popup-manager.service';
import { BetslipBetBuilderPick } from '../../../core/picks/betslip-bet-builder-pick';
import { BetslipGroupPick } from '../../../core/picks/betslip-group-pick';
import { BetslipPick } from '../../../core/picks/betslip-pick';

@Injectable()
export class BetBuilderUtilService {
    constructor(
        private betBuilderOverlayService: BetBuilderOverlayService,
        private betBuilderConfigService: BetBuilderConfigService,
        private redirectHelper: RedirectHelperService,
        private navigationRouterService: NavigationRouterService,
        private dispatcherService: DispatcherService,
        private popupManager: PopupManager,
    ) {}

    navigateToBABTab(pick: BetslipBetBuilderPick | BetslipGroupPick, headerTemplate: TemplateRef<any>): void {
        if (BetslipGroupPick.isPick(pick)) {
            this.navigateGroupedPick(pick, headerTemplate);
        } else {
            this.navigateBetBuilderPick(pick, headerTemplate);
        }
    }

    isBetBuilderOverlayOpen(pick: BetslipPick): boolean {
        return this.betBuilderConfigService.showEntainUiInOverlay(pick.sportId) && this.popupManager.isPopupInStack('bet-builder-overlay');
    }

    closeOverlay(): Promise<void> {
        return this.popupManager.closeByName(PopupAction.SilentClose, 'bet-builder-overlay');
    }

    private navigateGroupedPick(pick: BetslipGroupPick, headerTemplate: TemplateRef<any>): void {
        if (this.betBuilderConfigService.showEntainUiInOverlay(pick.sportId)) {
            const betBuilderEventModel = this.getBetBuilderEventModel(pick);
            betBuilderEventModel.betBuilderInfo = pick.groupInfo
                ? {
                      betBuilderId: pick.groupInfo.groupId,
                      betBuilderProvider: pick.groupInfo.provider,
                  }
                : null;

            this.betBuilderOverlayService.openBuilder({
                headerTemplate,
                event: betBuilderEventModel,
                source: BetBuilderTrackingValues.EditPickButton,
            });
        } else {
            this.popupManager.closeAll();
            this.navigateToTargetPick(pick, MarketUrlParam.Sgp);

            if (pick.eventId === this.navigationRouterService.getSelectedEventId) {
                this.dispatcherService.dispatch<SetTab>(DispatcherEvent.EventDetails.SetTab, SetTab.Sgp);
            }
        }
    }

    private navigateBetBuilderPick(pick: BetslipBetBuilderPick, headerTemplate: TemplateRef<any>): void {
        if (this.betBuilderConfigService.showEntainUiInOverlay(pick.sportId)) {
            const betBuilderEventModel = this.getBetBuilderEventModel(pick);
            betBuilderEventModel.sportcastId = pick.sportcastId;
            betBuilderEventModel.betBuilderTradingV2FixtureId = pick.event.betBuilderTradingV2FixtureId;

            this.betBuilderOverlayService.openBuilder({
                headerTemplate,
                event: betBuilderEventModel,
                source: BetBuilderTrackingValues.EditPickButton,
                isOverlay: true,
            });
        } else {
            this.navigateToTargetPick(pick, MarketUrlParam.BetBuilder);
            if (pick.eventId === this.navigationRouterService.getSelectedEventId) {
                this.dispatcherService.dispatch<SetTab>(DispatcherEvent.EventDetails.SetTab, SetTab.BetBuilder);
            } else {
                this.popupManager.closeAll();
            }
        }
    }

    private getBetBuilderEventModel(pick: BetslipBetBuilderPick | BetslipGroupPick): BetBuilderEventModel {
        return {
            id: pick.eventId,
            sport: {
                id: pick.sportId,
            },
            league: {
                id: pick.competitionId,
            },
            name: pick.eventName.name,
            betBuilderInfo: BetslipGroupPick.isPick(pick)
                ? { betBuilderId: pick.groupInfo.groupId, betBuilderProvider: pick.groupInfo.provider }
                : null,
        };
    }

    private navigateToTargetPick(pick: BetslipBetBuilderPick | BetslipGroupPick, param: MarketUrlParam): void {
        this.redirectHelper.goToEvent({ id: pick.eventId, name: pick.eventName.name }, param);
    }
}
